import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import axios from "../../../lib/axios";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { ITimesheet, ITimesheetStats } from "../../../types/api/timesheet";

export const useTimesheetList = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.TIMESHEET_LIST, { ...params, total: null }],
    queryFn: async () => {
      const res = await axios.get(API_ENDPOINTS.TIMESHEET_LIST, { params });
      return res.data;
    },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: ITimesheet) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useTimesheetStats = () =>
  useQuery<{ data: ITimesheetStats }>({
    queryKey: [API_ENDPOINTS.TIMESHEET_STATS],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.TIMESHEET_STATS); return res.data; },
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useTimesheetDetail = (id: number | null) =>
  useQuery({
    queryKey: [API_ENDPOINTS.TIMESHEET_DETAIL, id],
    queryFn: async () => { const res = await axios.get(`${API_ENDPOINTS.TIMESHEET_DETAIL}/${id}`); return res.data; },
    enabled: !!id,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useTimesheetApprove = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: number; notes?: string }) => {
      const res = await axios.post(API_ENDPOINTS.TIMESHEET_APPROVE, data);
      return res.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TIMESHEET_LIST] }); qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TIMESHEET_STATS] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};

export const useTimesheetReject = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: number; notes?: string }) => {
      const res = await axios.post(API_ENDPOINTS.TIMESHEET_REJECT, data);
      return res.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TIMESHEET_LIST] }); qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TIMESHEET_STATS] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};
